import { useCallback, useEffect, useMemo } from 'react';
import { Box, capitalize } from '@mui/material';
import Axios, { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';

interface UseSnackbarInterceptorOptions {
  displayErrors?: boolean;
  displaySuccess?: boolean;
}

export const useSnackbarInterceptor = ({
  displayErrors = true,
  displaySuccess = false
}: UseSnackbarInterceptorOptions = {}) => {
  const { enqueueSnackbar } = useSnackbar();
  const shouldIgnoreSuccessUrl = useMemo(() => shouldIgnoreUrl(SUCCESS_IGNORED_URLS), []);
  const shouldIgnoreErrorUrl = useMemo(() => shouldIgnoreUrl(ERROR_IGNORED_URLS), []);

  useEffect(() => {
    window['enqueueSnackbar'] = enqueueSnackbar;
  }, [enqueueSnackbar]);

  const onResponse = useCallback(
    (response: AxiosResponse) => {
      const url = response.config.url || '';

      if (response.config.method !== 'get' && !shouldIgnoreSuccessUrl(url, response)) {
        const messageMatch = SUCCESS_MESSAGES.find(successMessage => {
          if (successMessage.url.test(url)) {
            return successMessage.when?.(response) ?? successMessage;
          }

          return false;
        });

        enqueueSnackbar({
          variant: 'success',
          message: messageMatch?.message || 'Operation completed successfully.'
        });
      }

      return response;
    },
    [enqueueSnackbar, shouldIgnoreSuccessUrl]
  );

  const onResponseError = useCallback(
    error => {
      // Early reject promise if is canceled via Axios or is not an Axios error
      if (Axios.isCancel(error) || !Axios.isAxiosError(error)) return Promise.reject(error);

      const response = error.response;
      const url = response?.config.url || '';

      // Redirect to logout if session has expired
      if (response?.data === 'You are logged out') {
        window.location.href = '/logout';
      }

      if (response && !shouldIgnoreErrorUrl(url, response)) {
        const messageMatch = ERROR_MESSAGES.find(errorMessage => {
          if (errorMessage.url.test(url)) {
            return errorMessage.when?.(response) ?? errorMessage;
          }

          return false;
        });

        if (messageMatch) {
          enqueueSnackbar({
            variant: 'error',
            message: messageMatch.message
          });
        } else if (response.status === 400 || response.status === 409) {
          console.error(buildPreRenderedList(buildErrorMsg(response)));
          enqueueSnackbar({
            variant: 'error',
            message: displayErrors
              ? buildPreRenderedList(buildErrorMsg(response))
              : `An error has occurred while processing your request. Please try again or contact Support if the issue continues.`
          });
        } else if (response.status === 403) {
          enqueueSnackbar({
            variant: 'error',
            message: "You don't have rights to perform this action."
          });
        } else if (response.status === 413) {
          enqueueSnackbar({
            variant: 'error',
            message: 'The file you are trying to upload is too large.'
          });
        } else if (response.status >= 500) {
          console.error(`Server Error ${response.status}:`, response);
          enqueueSnackbar({
            variant: 'error',
            message: `An error has occurred while processing your request. Please try again or contact Support if the issue continues.`
          });
        } else {
          console.error('Request Error:', response);
          enqueueSnackbar({
            variant: 'error',
            message:
              'An error has occurred while processing your request. Please try again or contact Support if the issue continues.'
          });
        }
      }

      return Promise.reject(error);
    },
    [enqueueSnackbar, shouldIgnoreErrorUrl, displayErrors]
  );

  useEffect(() => {
    const snackbarInterceptor = Axios.interceptors.response.use(
      displaySuccess ? onResponse : undefined,
      onResponseError
    );

    return () => {
      Axios.interceptors.response.eject(snackbarInterceptor);
    };
  }, [onResponseError, onResponse, displaySuccess]);
};

const shouldIgnoreUrl = (urls: RegExp[]) => {
  return (url: string, response: AxiosResponse): boolean => {
    if (response.config.headers['X-Ignore-Toast'] === '1') return true;

    return url ? !!urls.find(re => re.test(url)) : false;
  };
};

const buildErrorMsg = ({ data }) => {
  const messages: string[] = [];

  if (typeof data === 'string') return [data];

  if (!Array.isArray(data)) {
    Object.entries<string | string[] | Record<string, unknown>>(data).forEach(([key, value]) => {
      const description = Array.isArray(value) ? buildErrorMsg({ data: value }) : value;

      if (typeof description === 'string') {
        if (key === 'non_field_errors' || key === 'detail') {
          messages.push(description);
        } else {
          messages.push(`${capitalize(key)}: ${description}`);
        }
      } else {
        messages.push(...buildErrorMsg({ data: description }));
      }
    });
  } else {
    data.forEach(item => {
      if (typeof item === 'string') {
        messages.push(item);
      }
    });
  }

  return messages;
};

const buildPreRenderedList = (list: string[]) =>
  list.length > 1 ? (
    <Box component="ul" m={0}>
      {list.map(item => (
        <Box component="li" key={item}>
          {item}
        </Box>
      ))}
    </Box>
  ) : (
    list[0]
  );

interface Message {
  url: RegExp;
  message: string;
  when?: (response: AxiosResponse) => boolean;
}

const SUCCESS_IGNORED_URLS: RegExp[] = [
  /\/asset_api\/v1\/asset_healthcheck\/partner/,
  /\/v1\/flex_runs/,
  /\/v1\/company_templates/,
  /\/v1\/mssp\/team_modules/,
  /\/v1\/flex_packages\/.*\/generate_token/,
  /\/v1\/referral/,
  /\/v1\/emm\/ready\/metrics\/vuln_risk_breakdown/,
  /\/v1\/emm\/ready\/vulnerabilities\/asset_vulns/,
  /\/v1\/emm\/ready\/vulnerabilities\/asset_vuln_count/,
  /\/v1\/emm\/ready\/metrics\/home_summary/
];

const SUCCESS_MESSAGES: Message[] = [
  {
    url: new RegExp('/v1/integrations/.*/test'),
    message: 'Test message sent'
  },
  {
    url: new RegExp('/v1/invitations'),
    message: 'Invitation sent'
  },
  {
    url: new RegExp('/v1/test_scenarios/*'),
    message: 'Operation completed successfully.'
  },
  {
    url: new RegExp('/v1/reports/.*/email'),
    message: 'Report sent successfully'
  },
  {
    url: new RegExp('/v1/company_configuration/'),
    message: 'Configuration updated successfully'
  },
  {
    url: new RegExp('/v1/company_connectors$'),
    message: 'Integration was configured successfully, and ready to be enabled'
  },
  {
    url: new RegExp('/v1/company_connectors/.*'),
    message: 'Integration is being enabled, it might take up to 1-2 min',
    when: response => {
      if (response.config.data === undefined) {
        return false;
      }
      const data = JSON.parse(response.config.data);

      const isDescriptionChange = Object.prototype.hasOwnProperty.call(data, 'description');
      const isDisplayNameChange = Object.prototype.hasOwnProperty.call(data, 'display_name');
      const isConfiguring = Object.prototype.hasOwnProperty.call(data, 'configuration');
      const showDefaultMessage = (isDescriptionChange || isDisplayNameChange) && !isConfiguring;

      return response.config.method === 'patch' && response?.data?.enabled && !showDefaultMessage;
    }
  },
  {
    url: new RegExp('/v1/reports/.*/report_pdf'),
    message: 'Report generation in progress',
    when: response => response.config.method === 'post'
  },
  {
    url: new RegExp('/v1/reports/.*/report_pdf'),
    message: 'Report generation canceled',
    when: response => response.config.method === 'delete'
  }
];

const ERROR_IGNORED_URLS: RegExp[] = [
  /\/v1\/users\/change_password/,
  /\/v1\/mfa(\/[^/]+)?(\/reset)?$/,
  /\/v1\/assessments\/.*\/activate$/,
  /\/v1\/scenario_jobs\/in_progress$/,
  /\/v1\/scenario_jobs\/in_progress_by_asset$/,
  /\/v1\/blueprint_templates\/upload$/
];

const ERROR_MESSAGES: Message[] = [
  {
    url: /\/v1\/emm\/discovery\/integration\/.*/,
    message: 'Invalid credentials.',
    when: response => response.status === 400 && response?.data?.status === 'TEST_FAILED'
  }
];
